import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiActivity } from 'react-icons/fi';
import { apiService } from '@/utils/api';
import AwardSVG from '@/assets/ic_award.svg';

// Calibration action constants (mirror Android naming)
const CalibrationActions = {
  FLOW_START_CALIBRATION: 2,
  FLOW_MONITOR_CALIBRATION: 3,
  FLOW_STOP_CALIBRATION: 4,
  FLOW_COMPLETED_CALIBRATION: 5,
} as const;

type Props = {
  deviceId: string;
  isVsd: boolean;
  onClose: () => void;
};

type MonitorItem = {
  speedLabel: string;
  flowRate: number | string | null;
  power: number | string | null;
  status: string;
};

type ParsedResponse = {
  actionText: string;
  statusText: string;
  items: MonitorItem[];
};

function parseCalibrationResponse(payload: any): ParsedResponse {
  if (!payload || typeof payload !== 'object') {
    return { actionText: '-', statusText: '-', items: [] };
  }

  const root = (payload?.data?.message) ?? (payload?.message) ?? payload;

  // 提取动作与状态文案，兼容多种字段命名
  let actionText = root?.actionText ?? root?.action_text ?? '-';
  let statusText = root?.statusText ?? root?.status_text ?? root?.event_text ?? '-';

  const items: MonitorItem[] = [];

  // 监控条目列表，兼容 monitor_items / monitorItems / items
  const monitorItems = root?.monitor_items ?? root?.monitorItems ?? root?.items ?? null;
  if (Array.isArray(monitorItems)) {
    for (const it of monitorItems) {
      let speedLabel: any = it?.category ?? it?.speed ?? it?.stage ?? it?.step ?? '-';
      if (typeof speedLabel === 'number') {
        speedLabel = `${speedLabel}%`;
      } else if (typeof speedLabel === 'string') {
        speedLabel = speedLabel.endsWith('%') ? speedLabel : (speedLabel ? `${speedLabel}%` : '-');
      }
      items.push({
        speedLabel,
        flowRate: it?.flowRate ?? it?.flow_rate ?? null,
        power: it?.power ?? null,
        status: it?.status ?? statusText ?? '-',
      });
    }
  } else {
    // 兼容原始数组字段
    const flowRates: any[] = root?.flow_rate_list ?? [];
    const powers: any[] = root?.power_list ?? [];
    const steps: any[] = root?.step_list ?? root?.steps ?? [];
    const len = Math.min(flowRates.length, powers.length, steps.length);
    for (let i = 0; i < len; i++) {
      const step = steps[i];
      const speedLabel = typeof step === 'number' ? `${step}%` : `${step ?? '-'}`;
      items.push({
        speedLabel,
        flowRate: flowRates[i] ?? null,
        power: powers[i] ?? null,
        status: statusText ?? '-',
      });
    }
  }

  // 代码映射兜底，兼容 action_code / event_code 等
  const actionCode = root?.action_code ?? root?.flow_calibrate_action ?? root?.action ?? null;
  const statusCode = root?.event_code ?? root?.flow_calibrate_event ?? root?.event ?? null;

  const actionMap = new Map<number, string>([
    [CalibrationActions.FLOW_START_CALIBRATION, 'Start'],
    [CalibrationActions.FLOW_MONITOR_CALIBRATION, 'Monitor'],
    [CalibrationActions.FLOW_STOP_CALIBRATION, 'Stop'],
    [CalibrationActions.FLOW_COMPLETED_CALIBRATION, 'Complete'],
  ]);

  const statusMap = new Map<number, string>([
    [0, 'Pending'],
    [1, 'Started'],
    [2, 'Running'],
    [3, 'Failed'],
    [4, 'Stop'],
    [5, 'Complete'],
  ]);

  if (actionText === '-' && typeof actionCode === 'number') {
    actionText = actionMap.get(Number(actionCode)) ?? '-';
  }
  if (statusText === '-' && typeof statusCode === 'number') {
    statusText = statusMap.get(Number(statusCode)) ?? '-';
  }

  return { actionText, statusText, items };
}

const PumpCalibrationSidebar: React.FC<Props> = ({ deviceId, isVsd, onClose }) => {
  const [phase, setPhase] = useState<'Ready' | 'Start' | 'Running' | 'Stopped' | 'Finish' | 'Done' | 'Failed'>('Ready');
  const [items, setItems] = useState<MonitorItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionText, setActionText] = useState<string>('-');
  const [statusText, setStatusText] = useState<string>('-');
  const firstMonitorCheck = useRef<boolean>(true);
  const pollTimer = useRef<any>(null);

  const canOperate = useMemo(() => isVsd, [isVsd]);

  useEffect(() => {
    if (!canOperate) return;
    // 初次打开仅请求一次监控，不进入轮询
    firstMonitorCheck.current = true;
    sendCalibrationRequest(CalibrationActions.FLOW_MONITOR_CALIBRATION);
    return () => {
      if (pollTimer.current) {
        clearTimeout(pollTimer.current);
        pollTimer.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendCalibrationRequest(action: number) {
    if (!deviceId) return;
    setLoading(true);
    var currentPhase = '';
    try {
      const res = await apiService.post<any>(`/api/v1/iot-shadow/device/${deviceId}/calibrateFlow`, { action });
      const parsed = parseCalibrationResponse(res ?? {});
      setActionText(parsed.actionText);
      setStatusText(parsed.statusText);
      setItems(parsed.items);
      currentPhase = parsed.statusText;
      console.log("res:", res);
      console.log('sendCalibrationRequest parsed', parsed);
      // 状态机：与 Android 保持一致，但首次打开不轮询
      if (parsed.actionText === 'Monitor') {
        if (firstMonitorCheck.current) {
          // 初次打开：保持 Ready，不进入轮询
          setPhase('Ready');
          firstMonitorCheck.current = false;
        } else if (parsed.statusText === 'Running' || parsed.statusText === 'Started') {
          setPhase('Running');
          // 进入轮询监控
          pollTimer.current = setTimeout(() => {
            sendCalibrationRequest(CalibrationActions.FLOW_MONITOR_CALIBRATION);
          }, 3000);
        } else if (parsed.statusText === 'Complete' || parsed.statusText === 'Stop') {
          // 请求已结束
          await sendCalibrationRequest(CalibrationActions.FLOW_COMPLETED_CALIBRATION);
        } else if (parsed.statusText === 'Failed') {
          setPhase('Failed');
        } else {
          setPhase('Ready');
        }
      } else if (parsed.actionText === 'Start') {
        setPhase('Start');
        if (parsed.statusText === 'Started' || parsed.statusText === 'Running') {
          pollTimer.current = setTimeout(() => {
            sendCalibrationRequest(CalibrationActions.FLOW_MONITOR_CALIBRATION);
          }, 3000);
        } else if (parsed.statusText === 'Failed') {
          setPhase('Failed');
        }
      } else if (parsed.actionText === 'Stop') {
        setPhase('Stopped');
      } else if (parsed.actionText === 'Complete') {
        setPhase('Finish');
      }
    } catch (e) {
      console.error('Pump calibration request failed', e);
      if (currentPhase === 'Started' || currentPhase === 'Running') {
        pollTimer.current = setTimeout(() => {
          sendCalibrationRequest(CalibrationActions.FLOW_MONITOR_CALIBRATION);
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  }

  function primaryCtaLabel(): string {
    switch (phase) {
      case 'Start':
        return 'Stop';
      case 'Running':
        return 'Stop';
      case 'Stopped':
      case 'Finish':
        return 'Finish';
      case 'Done':
        return 'Done';
      default:
        return 'Continue';
    }
  }

  async function onPrimaryCtaClick() {
    if (!canOperate) return;
    const label = primaryCtaLabel();
    if (label === 'Continue') {
      await sendCalibrationRequest(CalibrationActions.FLOW_START_CALIBRATION);
    } else if (label === 'Stop') {
      await sendCalibrationRequest(CalibrationActions.FLOW_STOP_CALIBRATION);
    } else if (label === 'Finish') {
      setPhase('Done');
    } else if (label === 'Done') {
      onClose();
    }
  }

  function resetToReady() {
    setPhase('Ready');
    setItems([]);
    setActionText('-');
    setStatusText('-');
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Sidebar */}
      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white dark:bg-gray-900 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <FiActivity className="text-primary-500" />
            <div>
              <div className="text-base font-semibold">Pump Calibration</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <span>{actionText}</span>
                <span>•</span>
                <span className={statusBadgeClass(statusText)}>{statusText}</span>
              </div>
            </div>
          </div>
          <button className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onClose} aria-label="Close">
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 py-4">
          {!isVsd && (
            <div className="px-3 py-2 rounded-md bg-amber-100 text-amber-700 text-sm">Flow calibration is not supported for non‑VSD pumps.</div>
          )}

          {isVsd && (
            <>
              {phase === 'Ready' && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-5 flex flex-col items-start gap-3">
                  <img src={AwardSVG} alt="Calibration hint" className="w-18 h-18 self-center" />
                  <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                    For best results, please ensure that the filter has been cleaned or backwashed.
                  </p>
                </div>
              )}

              {(phase === 'Start' || phase === 'Running' || phase === 'Stopped' || phase === 'Finish' || phase === 'Done' || phase === 'Failed') && items.length > 0 && (
                <div className="space-y-3">
                  {items.map((it, idx) => (
                    <div key={idx} className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{it.speedLabel || '-'}</div>
                        <span className={statusBadgeClass(it.status)}>{it.status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-300">Flow Rate</span>
                          <span className="text-sm font-medium">{it.flowRate != null ? `${it.flowRate}` : '-'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-300">Power</span>
                          <span className="text-sm font-medium">{it.power != null ? `${it.power}` : '-'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">{loading ? 'Processing…' : ' '}</div>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-2 rounded-md text-sm ${isVsd ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed'}`}
              onClick={onPrimaryCtaClick}
              disabled={!isVsd}
            >
              {primaryCtaLabel()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PumpCalibrationSidebar;


function statusBadgeClass(status: string): string {
  const t = (status || '').toLowerCase();
  if (t === 'complete' || t === 'completed') {
    return 'px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
  }
  if (t === 'running' || t === 'started') {
    return 'px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700';
  }
  if (t === 'pending') {
    return 'px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-700';
  }
  if (t === 'failed') {
    return 'px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
  }
  if (t === 'stop' || t === 'stopped') {
    return 'px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700';
  }
  return 'px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-700';
}